import type { Logger } from '@vp/ubik-logging'

import { saveWork } from '../../clients/WorkEntityClient'
import type { Work } from '../../types/Work'
import type { Project } from '../../types/Project'
import type { Quantity } from '../../types/Inventory'

/**
 * Saves a single work with updated quantities
 *
 * @param cartTenant - The cart tenant identifier
 * @param work - The work object to save
 * @param inputQuantity - The new input quantity
 * @param totalQuantity - The new total quantity
 * @param isCareAgent - Whether the user is a CARE agent
 */
export async function saveWorkWithQuantity(
  cartTenant: string,
  work: Work,
  inputQuantity: Quantity,
  totalQuantity: number,
  isCareAgent: boolean,
  authorizationHeader: string,
  logger: Logger,
): Promise<void> {
  work.resources['qty'] = JSON.stringify(inputQuantity)
  work.merchandising.quantity = totalQuantity
  await saveWork(cartTenant, work, isCareAgent, work.ownerId, authorizationHeader, logger)
}

/**
 * Saves all works that have quantity changes
 *
 * @param cartTenant - The cart tenant identifier
 * @param projects - Array of projects to process
 * @param worksData - Map of work IDs to their quantity data
 * @param isCareAgent - Whether the user is a CARE agent
 */
export async function saveWorksBeforeAddToCart(
  cartTenant: string,
  projects: Project[],
  worksData: Record<string, { inputQuantity?: Quantity; totalQuantity?: number }>,
  isCareAgent: boolean,
  authorizationHeader: string | undefined,
  logger: Logger,
): Promise<void> {
  const tasks = projects.map(async (project) => {
    const { work } = project
    const { inputQuantity, totalQuantity } = worksData[work.workId] ?? {}

    if (inputQuantity !== undefined && totalQuantity !== undefined && authorizationHeader) {
      await saveWorkWithQuantity(cartTenant, work, inputQuantity, totalQuantity, isCareAgent, authorizationHeader, logger)
    }
  })

  await Promise.all(tasks)
}
